import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { CommentsReportReasons } from "../../../../api/types/commentsReportReasons.types";
import { useCommentReportMutation } from "../../../../api/mutations/useCommentReportMutation";

interface CommentReportModalProps {
  open: boolean;
  onClose: () => void;
  commentId: number;
  reportReasons: CommentsReportReasons.ReportReason[];
}

export const CommentReportModal = ({ open, onClose, commentId, reportReasons = [] }: CommentReportModalProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "reportModal" });
  const [selectedReason, setSelectedReason] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const { mutate: reportComment, isPending } = useCommentReportMutation();

  const handleReasonChange = (event: SelectChangeEvent<number>) => {
    setSelectedReason(event.target.value as number);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const handleSubmit = () => {
    if (selectedReason === "") return;

    reportComment(
      {
        commentId,
        reasonId: selectedReason,
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          onClose();
          setSelectedReason("");
          setDescription("");
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ "& .MuiDialog-paper": { borderRadius: "12px", padding: "10px" } }}
    >
      <DialogTitle>{t("title", "Report Comment")}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth>
          <Typography variant="subtitle2" gutterBottom>
            {t("reasonLabel", "Reason for reporting")}
          </Typography>
          <Select value={selectedReason} onChange={handleReasonChange} displayEmpty fullWidth>
            <MenuItem value="" disabled>
              {t("selectReason", "Select a reason")}
            </MenuItem>
            {reportReasons.map((reason) => (
              <MenuItem key={reason.id} value={reason.id}>
                {reason.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <Typography variant="subtitle2" gutterBottom>
            {t("descriptionLabel", "Additional details (optional)")}
          </Typography>
          <TextField
            multiline
            rows={4}
            value={description}
            onChange={handleDescriptionChange}
            placeholder={t("descriptionPlaceholder", "Provide additional context...")}
            fullWidth
            sx={{ "& .MuiInputBase-root.MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t("cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={selectedReason === "" || isPending}
        >
          {isPending ? t("submitting", "Submitting...") : t("submit", "Submit Report")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
